import { useState, useEffect } from "react";
import { fetchActivePersonnel } from "../../utils/dataReceiver";

/**
 * Fetch and manage state for the Active Personnel modal.
 *
 * Fires once on mount. Cancels the in-flight request automatically if the
 * component unmounts before the response arrives (AbortController cleanup).
 *
 * @returns {{
 *   personnel: Array<{
 *     employeeName:   string,
 *     currentJob:     string | null,
 *     lastUpdated:    string | null,   - ISO-8601 or null
 *     locationStatus: "Active" | "No Active Position" | "No Locations Found"
 *   }>,
 *   count:   number,
 *   loading: boolean,
 *   error:   string | null,
 * }}
 */

let _cache = null;
let _cacheTime = 0;
const CACHE_TTL_MS = 60_000; // 1 minute

const useActivePersonnel = () => {
  const [personnel, setPersonnel] = useState(_cache?.personnel ?? []);
  const [count, setCount] = useState(_cache?.count ?? 0);
  const [loading, setLoading] = useState(!_cache);
  const [error, setError] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    const isStale = !_cache || Date.now() - _cacheTime > CACHE_TTL_MS;
    if (!isStale) return; // already fresh

    const load = async () => {
      try {
        const data = await fetchActivePersonnel(controller.signal);
        _cache = data;
        _cacheTime = Date.now();
        setPersonnel(data.personnel);
        setCount(data.count);
      } catch (err) {
        if (err.name === "AbortError") return;
        setError(err.message ?? "Failed to load active personnel.");
      } finally {
        setLoading(false);
      }
    };
    load();
    return () => controller.abort();
  }, []);

  return { personnel, count, loading, error };
};
export default useActivePersonnel;
